import {
  createFileRoute,
  Outlet,
  redirect,
  useRouter,
} from '@tanstack/react-router';
import { useIsMutating } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuthSessionAnalytics } from '@/hooks/auth-session-analytics.hook';
import { LOGOUT_MUTATION_KEY } from '@/hooks/login-logout.hook';
import { AudioChunkPlayerProvider } from '@/lib/audio-chunk-player';
import { DashboardSkeleton } from '@/components/web-fe/pages/dashboard/dashboard-skeleton';
import { rootLogger } from '@/lib/root-logger';
import {
  authResultSearchSchema,
  loginRedirectTarget,
} from '@/services/auth-service';
import { getSession } from '@/services/get-session';
import {
  markWebSessionEnd,
  rememberActiveSession,
} from '@/services/session-end';
import { useUserSessionInfo, useUserToken } from '@/services/user-service';
import { ImpersonationBanner } from '@/components/web-fe/admin/impersonation-banner';
import { NewFeatureAnnouncementDialog } from '@/components/web-fe/dialogs/new-feature-announcement-dialog';
import { Predicate } from 'effect';

const moduleLogger = rootLogger.child({ module: 'AuthenticatedRoute' });

export const Route = createFileRoute('/app/_authenticated')({
  // Declared here rather than on `/app/home` so every route an auth redirect
  // can land on inherits it — child routes merge their own schema on top.
  validateSearch: authResultSearchSchema,
  // The async session check in `beforeLoad` holds this route pending before the
  // `_dashboard` chrome (sidebar + AppBar) mounts. Show a dashboard-shaped shell
  // skeleton (sidebar + card grid) instead of a full-screen spinner, so the
  // first paint matches the layout the user is about to see.
  pendingComponent: DashboardSkeleton,
  pendingMs: 0,
  component: () => (
    <AudioChunkPlayerProvider>
      <Page />
    </AudioChunkPlayerProvider>
  ),
  beforeLoad: async ({ context, location, preload }) => {
    const check = await getSession();
    // A failed check is not a signed-out user — fail open and render. If the
    // session really is gone, the absence effect below notices and hands the
    // decision back to this guard via router.invalidate(), which re-runs it
    // client-side against the API.
    if (Predicate.isTagged(check, 'Unavailable')) return;
    if (Predicate.isTagged(check, 'Unauthenticated')) {
      moduleLogger.info({ location }, 'Not authorized, redirecting to login');
      markWebSessionEnd({ cause: 'route_guard_no_session' });
      // The signed-out user may not be the one who signs in next on this
      // browser, and `/app/login` is a client-side navigation away — nothing
      // else drops the previous user's cached queries before then.
      //
      // Never on a preload: `defaultPreload: 'intent'` runs this on hover,
      // and a preload doesn't navigate — teardown belongs to the real
      // navigation that follows.
      if (!preload) context.queryClient.clear();

      const redirectTo = loginRedirectTarget(location);

      throw redirect({
        to: '/app/login',
        search: {
          redirectTo,
        },
      });
    }
    // Records that this browser held a session, so a later forced logout can be
    // told apart from a first-time visitor opening an authenticated URL.
    rememberActiveSession();
  },
});

function Page() {
  const { isSessionAbsenceConfirmed } = useUserToken();
  const { userId } = useUserSessionInfo();
  const router = useRouter();
  const isLoggingOut = useIsMutating({ mutationKey: LOGOUT_MUTATION_KEY }) > 0;

  // Attaches PostHog/Sentry identity for every session, and reports social
  // sign-ins — which complete by redirect, so this is the only side that can
  // observe them at all.
  useAuthSessionAnalytics({
    authResult: Route.useSearch({ select: (s) => s.auth_result }),
    authProvider: Route.useSearch({ select: (s) => s.auth_provider }),
  });

  // Catches a session that dies while the user sits in the app, where no
  // navigation happens and `beforeLoad` above never re-runs.
  //
  // Hands the decision back to that guard rather than making its own: an
  // absent session in `authClient.useSession()` is a reason to re-check, not
  // grounds to act. `invalidate()` re-runs `beforeLoad`, which reads the
  // server and redirects only if the session is really gone — so a session
  // store that is merely behind (a sign-in the store has not observed yet)
  // costs one round trip instead of signing the user out.
  // @see test/e2e/flows/reauth-after-logout.spec.ts
  // Skipped during an intentional sign-out: that flow does its own teardown and
  // navigation, and a guard redirect racing it would land the user on
  // `/app/login` with a stray `redirectTo`.
  useEffect(() => {
    if (!isSessionAbsenceConfirmed || isLoggingOut) return;
    void router.invalidate();
  }, [isLoggingOut, isSessionAbsenceConfirmed, router]);

  // Positive counterpart of the absence effect: the guard only records the
  // had-a-session marker when its own check succeeds, so a check that came
  // back `Unavailable` on the user's first navigation would leave the marker
  // unset. The client session store settles the question; the marker write is
  // Ref-deduped, so repeat calls are free.
  useEffect(() => {
    if (userId) rememberActiveSession();
  }, [userId]);

  return (
    <>
      <ImpersonationBanner />
      <NewFeatureAnnouncementDialog userId={userId} />
      <Outlet />
    </>
  );
}
